import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { PostHogProvider as Provider } from "posthog-js/react";
import { env } from "@/env";
import { api } from "@/lib/backend";
import { extractResponseData } from "@/lib/backend/utils/extract";
import { posthog } from "@/lib/providers/posthog";
import { useSessionProvider } from "../../session/use-session";

export function PostHogProvider(props: React.ComponentProps<typeof Provider>) {
  const { session } = useSessionProvider();

  // Fetch organization details for the user
  const { data: organization } = useQuery({
    queryKey: ["organization", "my-organization"],
    queryFn: async () => {
      const response = await api.organization.getMyOrganization();
      return extractResponseData(response);
    },
    enabled: !!session?.data?.user?.id,
  });

  useEffect(() => {
    if (!session?.data?.user) {
      return;
    }

    const user = session.data.user as any;

    // Identify user with additional properties including environment
    posthog.identify(user.id, {
      email: user.email,
      emailVerified: user.emailVerified,
      name: user.name,
      image: user.image,
      firstname: user.firstname,
      lastname: user.lastname,
      environment: env.VITE_TARGET,
      organizationId: user.organizationId,
    });

    // Set up organization as a group
    if (user.organizationId && organization) {
      posthog.group("organization", user.organizationId, {
        name: organization.name,
        slug: organization.slug,
        environment: env.VITE_TARGET,
        employeeAccountsEnabled: organization.employeeAccountsEnabled,
      });
    }

    // Set super properties that will be included with all events
    posthog.register({
      environment: env.VITE_TARGET,
      organizationId: user.organizationId,
      organizationName: organization?.name,
    });
  }, [session, organization]);

  return <Provider client={posthog} {...props} />;
}
